import { ArrowDown, Check } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { columnLabel, friendlyRefs } from "@/components/agents/conversation/conversationTexts";
import type { QuestionItem } from "@/components/agents/conversation/types";
import { cn } from "@/lib/utils";

/** Trecho entre `*…*` vira destaque; espaço junto ao asterisco não conta. */
const HIGHLIGHT = /\*(\S(?:[^*]*\S)?)\*/g;

function QuestionText({ text }: { text: string }) {
  const { t } = useTranslation();
  const parts = friendlyRefs(t, text).split(HIGHLIGHT);
  return (
    <p className="whitespace-pre-wrap break-words font-sans text-[15px] leading-[1.6] text-white">
      {parts.map((part, index) =>
        // O `split` com grupo de captura alterna: texto, destaque, texto, destaque…
        index % 2 === 1 ? (
          <mark
            key={index}
            className="rounded bg-[var(--exed-gold)] px-1.5 py-px font-mono font-semibold text-[var(--exed-navy)]"
          >
            {part}
          </mark>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </p>
  );
}

/** A linha que aponta para o campo único do terminal: tudo se responde digitando lá. */
export function AnswerInFieldHint() {
  const { t } = useTranslation();
  return (
    <p className="flex items-center gap-1.5 text-[13px] font-medium text-[var(--exed-gold)]">
      <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
      {t("Answer in the field below.")}
    </p>
  );
}

/**
 * A pergunta do agente, como cartão de alerta do chat (Claude Design) na linha do tempo.
 * Somente leitura: não há botão de resposta. As escolhas (options, sim/não) e os campos
 * do mapeamento aparecem como texto, e a pessoa responde digitando no campo único do
 * terminal, que responde a esta pergunta (`reply_to`). O log confirma a resposta.
 */
export function QuestionCard({
  item,
  pendingAnswer = false,
  disabled = false,
  watching = false,
}: {
  item: QuestionItem;
  pendingAnswer?: boolean;
  disabled?: boolean;
  /** Quem vê não é o executor: a pergunta não é dele, então o rótulo não a pede a ele. */
  watching?: boolean;
}) {
  const { t } = useTranslation();
  const resolved = item.answered || item.superseded;
  const locked = disabled || resolved || pendingAnswer;

  return (
    <div
      data-testid={`conversation-question-${item.seq}`}
      className="mt-2 flex max-w-[88%] self-start"
    >
      <div
        data-testid={resolved ? "chat-answered" : "chat-alert"}
        className={cn(
          "flex min-w-0 flex-1 gap-3 rounded-lg border border-l-[3px] px-4 py-3.5 font-sans",
          resolved
            ? "border-[rgba(255,255,255,0.08)] border-l-[rgba(255,255,255,0.3)] bg-[rgba(255,255,255,0.03)]"
            : "border-[rgba(255,200,61,0.2)] border-l-[var(--exed-gold)] bg-[rgba(255,200,61,0.08)]",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[14px] font-bold",
            resolved
              ? "bg-[rgba(255,255,255,0.15)] text-[rgba(255,255,255,0.7)]"
              : "bg-[var(--exed-gold)] text-[var(--exed-navy)]",
          )}
        >
          {resolved ? <Check className="h-3.5 w-3.5" /> : "?"}
        </span>
        <div className="min-w-0 flex-1 space-y-2.5">
          <p
            className={cn(
              "font-mono text-[10px] font-semibold uppercase leading-normal tracking-[0.12em]",
              resolved ? "text-[rgba(255,255,255,0.5)]" : "text-[var(--exed-gold)]",
            )}
          >
            {item.answered
              ? t("Answered")
              : item.superseded
                ? t("A newer question replaced this one.")
                : watching
                  ? t("Waiting for the executor's answer")
                  : t("The agent needs your answer")}
          </p>
          <QuestionText text={item.text} />
          {!item.answered && item.format === "options" && (
            <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-[#d6dde8]">
              {item.choices.map((choice) => (
                <li key={choice}>{choice}</li>
              ))}
            </ul>
          )}
          {!item.answered && item.format === "yes_no" && (
            <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-[#d6dde8]">
              <li>{t("Yes")}</li>
              <li>{t("No")}</li>
            </ul>
          )}
          {!item.answered && item.format === "mapping" && (
            <ul className="space-y-2 text-[13px] text-[#d6dde8]">
              {item.mappingFields.map((field) => (
                <li key={field.ref} className="space-y-1.5 break-words">
                  <span className="font-medium">{friendlyRefs(t, field.label)}</span>
                  {field.choices.length > 0 && (
                    <span className="flex flex-wrap gap-1.5">
                      {field.choices.map((choice) => (
                        <span
                          key={choice}
                          className="rounded-full border border-[rgba(255,255,255,0.18)] bg-[rgba(255,255,255,0.07)] px-2.5 py-0.5 text-[12px] text-[#e6ecf5]"
                        >
                          {columnLabel(t, choice)}
                        </span>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!locked && <AnswerInFieldHint />}
        </div>
      </div>
    </div>
  );
}
